import React from "react";
import { ArrowRight } from "lucide-react";

/**
 * Reusable Button component with multiple variants
 *
 * @example
 * // Primary button (default)
 * <Button>Click me</Button>
 *
 * // Secondary button
 * <Button variant="secondary">Learn more</Button>
 *
 * // Outline button
 * <Button variant="outline">Cancel</Button>
 *
 * // With icon
 * <Button showArrow>Get started</Button>
 *
 * // Full width
 * <Button fullWidth>Submit</Button>
 *
 * // As link
 * <Button href="/contact">Contact us</Button>
 */

interface ButtonProps {
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline";
  size?: "sm" | "md" | "lg";
  href?: string;
  fullWidth?: boolean;
  showArrow?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: "button" | "submit" | "reset";
  className?: string;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = "primary",
  size = "md",
  href,
  fullWidth = false,
  showArrow = false,
  disabled = false,
  onClick,
  type = "button",
  className = "",
}) => {
  const baseStyles =
    "inline-flex items-center justify-center gap-2 font-semibold tracking-wide uppercase text-sm transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed btn-press";

  const variants = {
    primary:
      "bg-primary hover:bg-secondary text-white shadow-md hover:shadow-lg",
    secondary:
      "bg-text-primary hover:bg-text-secondary text-white shadow-md hover:shadow-lg",
    outline:
      "border-2 border-primary hover:bg-primary hover:text-white text-primary",
  };

  const sizes = {
    sm: "px-5 py-2.5 text-xs",
    md: "px-7 py-3 text-sm",
    lg: "px-8 py-4 text-sm",
  };

  const classes = [
    baseStyles,
    variants[variant],
    sizes[size],
    fullWidth ? "w-full" : "",
    className,
  ].join(" ");

  const content = (
    <>
      <span>{children}</span>
      {showArrow && <ArrowRight className="w-4 h-4" aria-hidden="true" />}
    </>
  );

  if (href) {
    return (
      <a href={href} className={classes}>
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={classes}
    >
      {content}
    </button>
  );
};

export default Button;
